import { Grid, InputLabel, styled, TextField, Tooltip, tooltipClasses } from "@mui/material";
import EAIcon from "components/common/EAIcon";
import useStyles from "../../../style/js-style/components/thirdparties/PanelOwlinStyle";
import i18next from "i18next";
import { useContext, useEffect, useState } from "react";
import { Context } from "states/Store";

import { Controller, useFormContext } from "react-hook-form";
import EAAutoCompleteInput from "components/inputs/EAAutoCompleteInput";
import { activateOwlin } from "services/edge/OwlinService";
import { ERROR } from "services/common/Constants";
import { isEmpty } from "lodash";
import EAButton from "components/common/EAButton";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils";

const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.grey.grey500,
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

const OwlinForm = ({ thirdPartyId, remainingTagsNb, isDialogMode = false, owlinData, mapTagsToOptions, setData, initialOptions, setHasError }) => {
    const { classes } = useStyles();
    const [state, dispatch] = useContext(Context);
    const [options, setOptions] = useState([]);
    const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);


    const isValidUrl = (urlString) => {
        if (isEmpty(urlString)) {
            return true;
        }
        const urlPattern = new RegExp('^(https?:\\/\\/){1}' + // validate protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
            '(\\#[-a-z\\d_]*)?$', 'i'); // validate fragment locator
        const isValid = !!urlPattern.test(urlString);
        setHasError(!isValid);
        return isValid;
    }

    useEffect(() => {
        if (initialOptions) {
            setOptions(initialOptions);
        }
    }, [initialOptions]);


    const {
        control,
        getValues,
        setError,
        formState
    } = useFormContext();

    const activateOwlinHandler = async () => {
        const url = getValues("owlinUrl");
        if (isEmpty(url)) {
            setError("owlinUrl", {
                message: i18next.t("infos.required"),
            })
        } else {
            try {
                await activateOwlin(
                    state.account.id,
                    thirdPartyId,
                    null
                );
                setData({ ...owlinData, status: "PENDING" });
            } catch (error) {
                const err = error?.message?.messages?.error[0];
                dispatch({
                    type: "ALERT",
                    alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
                });
            }
        }
    };

    const onChange = (target) => {
        setOptions(mapTagsToOptions(target));
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12} container className={classes.formContainer}>
                    <Grid item xs={3} className={classes.inputGrid}>
                        <InputLabel className={classes.inputLabel}>
                            {`${i18next.t("owlin.form.url")}`}
                            <LightTooltip title={i18next.t("owlin.form.urlTooltip")} arrow placement="top">
                                <span>
                                    <EAIcon icon={"info-rounded"} className={classes.infoIcon} />
                                </span>
                            </LightTooltip>
                        </InputLabel>
                    </Grid>
                    <Grid item xs={4}>
                        <Controller
                            control={control}
                            name="owlinUrl"
                            defaultValue=""
                            rules={{
                                validate: (value) => { return isValidUrl(value) ? null : i18next.t("owlin.form.urlError") }
                            }}
                            render={({ field }) => (
                                <TextField
                                    id="url-input-id"
                                    error={!!formState.errors.owlinUrl}
                                    fullWidth
                                    placeholder={i18next.t("owlin.form.urlPlaceHolder")}
                                    disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_OWLIN_SOURCE)}
                                    {...field}
                                    helperText={
                                        !!formState.errors.owlinUrl
                                            ? formState.errors.owlinUrl.message
                                            : ""
                                    }
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        {!isDialogMode && (
                            <EAButton
                                type="button"
                                colorVariant="primaryLight"
                                onClick={activateOwlinHandler}
                                disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_OWLIN_SOURCE)}
                                className={classes.button}
                                customStyle={{ boxShadow: "none" }}
                                content={
                                    <span className={classes.buttonContent}>
                                        <EAIcon icon="newEnrichment" />
                                        {i18next.t("owlin.form.activateLabel")}
                                    </span>}
                            />)}
                    </Grid>
                </Grid>
                <Grid item xs={12} container className={classes.formContainer}>
                    <Grid item xs={3} className={classes.inputGrid}>
                        <InputLabel className={classes.inputLabel}>
                            {`${i18next.t("owlin.form.tags")}`}
                            <LightTooltip title={i18next.t("owlin.form.tagsTooltip")} arrow placement="top">
                                <span>
                                    <EAIcon icon={"info-rounded"} className={classes.infoIcon} />
                                </span>
                            </LightTooltip>
                        </InputLabel>
                    </Grid>
                    <Grid item xs={8}>
                        <div>
                            <EAAutoCompleteInput
                                fieldName="owlinTags"
                                options={options}
                                placeholder={i18next.t("owlin.form.tagsPlaceHolder")}
                                variant="outlined"
                                multiple
                                limitTags={10}
                                freeSolo
                                maxValues={10}
                                disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_OWLIN_SOURCE)}
                                clearPlaceHolderOnInput={true}
                                onChange={onChange} />
                            <span className={classes.tagsCaption}>{i18next.t("owlin.form.tagsCaption", { nbTag: remainingTagsNb })}</span>
                        </div>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )

}
export default OwlinForm;
